import React, {
    useEffect,
    useState,
} from 'react';
import { DateTime } from 'luxon';
import { Dropdown as BootstrapDropdown } from 'react-bootstrap';
import Calendar from '../../Calendar/Calendar';
import Button from '../../Button/Button';
import Search from '../../Search/Search';
import './DropdownCalendar.css';
import { Caption3, Paragraph2 } from '../../../theme/styles';
import { translate } from '../../../translations';

/**
 * @summary Dropdown Calendar is a Controlled component, meaning that it will need certain props passed down from parent element for this component to work.
 * P.S. any dates that comes in/out from this component require ISO format (1975-08-19T23:15:30.000Z) to work.
 *
 * @param uniqueKey -- (REQUIRED)
 * @param userLocale
 *
 * @param startDate -- (REQUIRED) startDate with ISO format (1975-08-19T23:15:30.000Z)
 * @param endDate -- (REQUIRED) endDate with ISO format (1975-08-19T23:15:30.000Z)
 * @param calendarConfig -- (OPTIONAL) optional config to render options below calendar
 *      @example config: `[{ key: 1, text: '', type: 'link' or 'default', startDate: '1975-08-19T23:15:30.000Z', endDate: '1975-08-19T23:15:30.000Z'}]`
 *      @see '../src/work-hub/src/constants/WorkGridCalendar/WorkGridCalendar' const workGridDueDateCalendarConfig;
 *
 * @param onDropdownCalendarClick -- callback function that gets called after 2nd calendar click, use this to save output to state and pass back via startDate and endDate props
 *      @example onDropdownCalendarClick = (dateOptions) => setState([dateOptions[0], dateOptions[1]]);
 *          - output: ['1975-08-19T23:15:30.000Z', '1975-08-19T23:15:30.000Z']
 * @param dropdownCloseFunction -- function that close dropdown
 *
 * @return {JSX.Element}
 * @constructor
 */

export function DropdownCalendar({
    uniqueKey,
    userLocale,
    startDate,
    endDate,
    calendarConfig,
    onDropdownCalendarClick,
    dropdownCloseFunction,
    minDate,
    maxDate,
    limitDays,
    onDropdownCalendarSearchClick,
    onDropdownCalendarSearchClear,
}) {
    const [selectedDate, setSelectedDate] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [activeStartDate, setActiveStartDate] = useState(null);

    useEffect(() => {
        let selectedDateValue = null;
        if (startDate && endDate) {
            selectedDateValue = [new Date(startDate), new Date(endDate)];
        }

        setSelectedDate(selectedDateValue);
    }, [startDate, endDate]);

    useEffect(() => {
        setActiveStartDate(startDate ? new Date(startDate) : new Date());
    }, [startDate]);

    const calendarLinks = (calendarConfig && Object.values(calendarConfig).filter(({ type }) => type === 'link')) || [];
    const calendarDefault = (calendarConfig && Object.values(calendarConfig).filter(({ type }) => type === 'default')) || [];
    const searchDefault = (calendarConfig && Object.values(calendarConfig).filter(({ type }) => type === 'search')) || [];

    const selectedMaxDate = (selectedDate && selectedDate[0] && !selectedDate[1] && limitDays > 0)
        ? new Date(DateTime.fromJSDate(selectedDate[0]).plus({ days: limitDays }))
        : maxDate;

    // Single Day Selection
    const onClickDay = (value) => {
        const [currentStartDate, currentEndDate] = selectedDate || [];
        // if currentEndDate exist (meaning has 2 Date Ranges), replace that with single Date (Reset the value if you have 2 Date Ranges).
        setSelectedDate(currentStartDate && !currentEndDate ? [currentStartDate, value] : [value]);
        setSearchText('');
    };

    // Date Range Selection
    const onChangeDay = (values) => {
        const isoDateRanges = values.map((dateRange) => dateRange.toISOString());
        onDropdownCalendarClick(isoDateRanges);
        setSearchText('');
    };

    // Workaround for a bug where view does not change to selectedDate on clear filter
    // https://github.com/wojtekmaj/react-calendar/issues/342#issuecomment-716738084
    const onActiveStartDateChange = ({ activeStartDate: calendarStartDate }) => {
        setActiveStartDate(calendarStartDate);
    };

    const onLinkClick = (links) => {
        // need to close the dropdown first and wait in case only 1 date is clicked, but no closing date (fix in Dropdown.js)
        setTimeout(() => {
            dropdownCloseFunction();
        }, 100);
        // after click fix is run, now set the new values
        setTimeout(() => {
            onDropdownCalendarClick([links.startDate, links.endDate]);
            setSearchText('');
        }, 200);
    };

    const onSearchChange = (event) => {
        setSearchText(event.target.value);
    };

    const onSearchClear = () => {
        setSearchText('');
        if (onDropdownCalendarSearchClear) {
            onDropdownCalendarSearchClear(searchDefault);
        }
    };

    const onOKClick = () => {
        if (searchText && onDropdownCalendarSearchClick) {
            onDropdownCalendarSearchClick(searchText);
        }
        dropdownCloseFunction();
    };

    return (
        <BootstrapDropdown.Menu className="dropdownCalendarMenu">
            <div className="dropdownListMenuContent">
                <Calendar
                    selectedDate={selectedDate}
                    onClickDay={onClickDay}
                    onChange={onChangeDay}
                    returnValue="range"
                    selectRange
                    open
                    userLocale={userLocale}
                    disableClickOnNeighborMonth
                    minDate={minDate}
                    maxDate={selectedMaxDate}
                    activeStartDate={activeStartDate}
                    onActiveStartDateChange={onActiveStartDateChange}
                />
                <input type="hidden" id={`${uniqueKey}_hidden`} value={selectedDate} />
                { calendarConfig && (
                    <>
                        <div className="calendarDateLinks">
                            {Object.values(calendarLinks).map((links, index) => (
                                <Button
                                    key={links.key}
                                    variant="link"
                                    content={links.text}
                                    onClick={() => onLinkClick(Object.values(calendarLinks)[index])}
                                />
                            ))}
                        </div>
                        <div className="calendarDefaultLink">
                            {Object.values(calendarDefault).map((defaultLink) => (
                                <div key={defaultLink.key}>
                                    <Button
                                        key={`${defaultLink.key}Button`}
                                        variant="link"
                                        content={translate('default')}
                                        onClick={() => onLinkClick(Object.values(calendarDefault)[0])}
                                    />
                                    <span
                                        key={`${defaultLink.key}Text`}
                                        className="calendarDefaultText"
                                        style={Caption3}
                                    >
                                        {`(${defaultLink.text})`}
                                    </span>
                                </div>
                            ))}
                            {Object.values(searchDefault).map((defaultSearch) => (
                                <div key={defaultSearch.key} className="calendarSearchContainer">
                                    <Search
                                        includeBorder
                                        showClearIcon
                                        showSearchIcon
                                        placeholder={defaultSearch.text}
                                        placeholderStyle={Paragraph2}
                                        value={searchText}
                                        onChange={onSearchChange}
                                        onClearInput={onSearchClear}
                                        autoComplete="none"
                                    />
                                </div>
                            ))}
                            <Button
                                className="calendarOk"
                                content={translate('ok')}
                                onClick={onOKClick}
                            />
                        </div>
                    </>
                )}
            </div>
        </BootstrapDropdown.Menu>
    );
}

export default DropdownCalendar;
